/**
 * The upload page's queue, kept outside React so it outlives the page: one
 * pool for the whole tab (at most MAX_CONCURRENT_UPLOADS uploads, however
 * often the page remounts) and the rows of the files in it. A remounted page
 * reads the same rows, so running, queued and failed uploads show again with
 * live progress. Not in Redux: rows hold File and AbortController objects.
 *
 * While no page is attached, rows that need nothing more from the user are
 * dropped (finished, back to "ready" after a cancel or 4xx, not queued), as
 * the page dropped its state on unmount before. Failed rows stay for Retry.
 * Logout clears everything (resetUploadQueue).
 */
import { useSyncExternalStore } from 'react';
import type { LicenseLimitReason } from 'src/components/organisms/license/licenseLimitReason';
import type { UploadItem } from 'src/types/uploadItemTypes';
import { UploadPool, createUploadPool } from './uploadPool';

export const MAX_CONCURRENT_UPLOADS = 3;

export type UploadQueueState = {
  items: UploadItem[];
  /** A licence refusal (403) the dialog has not shown yet. */
  licenseLimit: LicenseLimitReason | null;
};

const EMPTY: UploadQueueState = { items: [], licenseLimit: null };

let state: UploadQueueState = EMPTY;
let pool: UploadPool<File> | undefined;
let attachedPages = 0;
const listeners = new Set<() => void>();

const setState = (next: UploadQueueState) => {
  if (next === state) return;
  state = next;
  listeners.forEach((listener) => listener());
};

/** While a pool has work, closing or reloading the tab asks first. */
const createPagePool = () => {
  const preventUnload = (event: BeforeUnloadEvent) => {
    event.preventDefault();
    event.returnValue = '';
  };
  return createUploadPool<File>(MAX_CONCURRENT_UPLOADS, {
    onActiveChange: (active) =>
      active
        ? window.addEventListener('beforeunload', preventUnload)
        : window.removeEventListener('beforeunload', preventUnload),
  });
};

/** Rows kept while no page shows them; `settled` has just finished its job. */
const stillNeeded = (item: UploadItem, settled?: File) =>
  item.state === 'UPLOADING' ||
  item.state === 'UPLOAD_FAILED' ||
  (item.state === 'SELECTED' && item.file !== settled && !!pool?.has(item.file));

const pruneDetached = (settled?: File) => {
  if (attachedPages > 0) return;
  const items = state.items.filter((item) => stillNeeded(item, settled));
  if (items.length !== state.items.length) setState({ ...state, items });
};

export const uploadQueue = {
  getState: () => state,

  subscribe: (listener: () => void) => {
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
    };
  },

  setItems: (update: (items: UploadItem[]) => UploadItem[]) => {
    const items = update(state.items);
    if (items !== state.items) setState({ ...state, items });
  },

  /** Updates the row of `file`; nothing when it was removed. */
  updateItem: (file: File, update: (item: UploadItem) => UploadItem) => {
    const index = state.items.findIndex((item) => item.file === file);
    if (index === -1) return;
    const items = [...state.items];
    items[index] = update(items[index]);
    setState({ ...state, items });
  },

  findItem: (file: File) => state.items.find((item) => item.file === file),

  /** Queues the upload of `file` in the one shared pool. */
  enqueue: (file: File, job: () => Promise<unknown>) => {
    pool ??= createPagePool();
    pool.add(file, async () => {
      try {
        await job();
      } finally {
        pruneDetached(file);
      }
    });
  },

  /** The page shows the queue; returns the detach for its unmount. */
  attach: () => {
    attachedPages++;
    let attached = true;
    return () => {
      if (!attached) return;
      attached = false;
      attachedPages--;
      pruneDetached();
    };
  },

  isAttached: () => attachedPages > 0,

  setLicenseLimit: (licenseLimit: LicenseLimitReason | null) => {
    if (licenseLimit !== state.licenseLimit) setState({ ...state, licenseLimit });
  },
};

/**
 * Logout (or the session gone in another tab): stop running uploads and
 * forget every row. Queued jobs find no row and skip; a fresh pool starts.
 */
export const resetUploadQueue = () => {
  state.items.forEach((item) => {
    if (item.state === 'UPLOADING') item.abortController.abort();
  });
  // The old pool drains on its own (aborted or skipped jobs) and then drops
  // its beforeunload handler.
  pool = undefined;
  setState(EMPTY);
};

export const useUploadQueue = () => useSyncExternalStore(uploadQueue.subscribe, uploadQueue.getState);
